/**
 * Game Navigation Service
 * Centralized service for all game navigation to ensure consistent URL generation
 * and proper variant masking across the entire application.
 */

import { generateCBGameUrl, generateGLDGameUrl } from "../utils/gameUrlUtils";

/**
 * Navigate to a game with proper URL masking
 * @param {Function} navigate - React Router navigate function
 * @param {Object|string} gameOrCode - Game object or game code string
 * @param {string} currency - Currency ('CB' or 'GLD')
 * @param {Object} options - Navigation options
 */
export function navigateToGame(
  navigate,
  gameOrCode,
  currency = "CB",
  options = {},
) {
  if (!navigate) {
    throw new Error("Navigate function is required");
  }

  // Handle both game objects and raw game codes
  let game;
  if (typeof gameOrCode === "string") {
    // If it's just a game code, create a minimal game object
    // Note: This won't have variant_group_id, so no masking will occur
    // This is intentional for backward compatibility with existing code
    game = { game_code: gameOrCode };
  } else if (gameOrCode && typeof gameOrCode === "object") {
    game = gameOrCode;
  } else {
    navigate("/games");
    return;
  }

  // Generate the appropriate URL using the centralized utility functions
  let gameUrl;
  if (currency === "GLD" || currency === "gld") {
    gameUrl = generateGLDGameUrl(game);
  } else {
    gameUrl = generateCBGameUrl(game);
  }

  // Navigate with options
  navigate(gameUrl, options);
}

/**
 * Navigate to CB game (default currency)
 * @param {Function} navigate - React Router navigate function
 * @param {Object|string} gameOrCode - Game object or game code string
 * @param {Object} options - Navigation options
 */
export function navigateToCBGame(navigate, gameOrCode, options = {}) {
  navigateToGame(navigate, gameOrCode, "CB", options);
}

/**
 * Navigate to GLD game (demo/fun mode)
 * @param {Function} navigate - React Router navigate function
 * @param {Object|string} gameOrCode - Game object or game code string
 * @param {Object} options - Navigation options
 */
export function navigateToGLDGame(navigate, gameOrCode, options = {}) {
  navigateToGame(navigate, gameOrCode, "GLD", options);
}

/**
 * Hook for game navigation (React hook pattern)
 * @param {Function} navigate - React Router navigate function
 * @returns {Object} Navigation functions
 */
export function useGameNavigation(navigate) {
  return {
    navigateToGame: (gameOrCode, currency = "CB", options = {}) =>
      navigateToGame(navigate, gameOrCode, currency, options),
    navigateToCBGame: (gameOrCode, options = {}) =>
      navigateToCBGame(navigate, gameOrCode, options),
    navigateToGLDGame: (gameOrCode, options = {}) =>
      navigateToGLDGame(navigate, gameOrCode, options),
  };
}

/**
 * Generate game URL without navigation (for href attributes, etc.)
 * @param {Object|string} gameOrCode - Game object or game code string
 * @param {string} currency - Currency ('CB' or 'GLD')
 * @returns {string} Game URL
 */
export function getGameUrl(gameOrCode, currency = "CB") {
  let game;
  if (typeof gameOrCode === "string") {
    game = { game_code: gameOrCode };
  } else if (gameOrCode && typeof gameOrCode === "object") {
    game = gameOrCode;
  } else {
    return "/games";
  }

  if (currency === "GLD" || currency === "gld") {
    return generateGLDGameUrl(game);
  } else {
    return generateCBGameUrl(game);
  }
}
